import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang, api } from "../store";
import { PageHead, Reveal } from "../components/Reveal";
import FilmModal from "../components/FilmModal";
import Footer from "../components/Footer";

const ROLES = ["tutti", "attore", "regista", "doppiatore", "stunt", "sceneggiatore"];
const FORMATS = ["tutti", "feature", "short", "tv"];
const FORMAT_BADGE = { feature: "FILM", short: "SHORT", tv: "TV" };

export default function Filmography() {
  const { lang, t } = useLang();
  const [params, setParams] = useSearchParams();
  const role = params.get("ruolo") || "tutti";
  const format = params.get("formato") || "tutti";
  const [films, setFilms] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const q = new URLSearchParams();
    if (role !== "tutti") q.set("role", role);
    if (format !== "tutti") q.set("format", format);
    api.get(`/films?${q.toString()}`).then((r) => setFilms(r.data)).catch(() => setFilms([]));
  }, [role, format]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value === "tutti") next.delete(key); else next.set(key, value);
    setParams(next);
  };

  const roleLabel = (r) => (r === "tutti" ? t("films.roleAll") : t(`films.${r}`));
  const formatLabel = (f) => (f === "tutti" ? t("films.formatAll") : t(`films.${f}`));

  return (
    <div className="min-h-screen" data-testid="filmography-page">
      <PageHead kicker="IMDB · NM6950929" title={t("films.title")} sub={t("films.sub")} />

      <div className="mx-auto max-w-[1600px] px-6">
        <div className="mb-10 flex flex-wrap items-center gap-x-8 gap-y-4 border-y border-white/10 py-5" data-testid="film-filters">
          <div className="flex flex-wrap gap-2">
            {ROLES.map((r) => (
              <button
                key={r}
                onClick={() => setFilter("ruolo", r)}
                data-testid={`film-filter-role-${r}`}
                className={`font-mono2 rounded-full border px-4 py-1.5 text-[11px] uppercase tracking-widest transition-colors ${role === r ? "border-amber-500 bg-amber-600/20 text-amber-400" : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"}`}
              >
                {roleLabel(r)}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {FORMATS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter("formato", f)}
                data-testid={`film-filter-format-${f}`}
                className={`rounded-full border px-4 py-1.5 text-[11px] font-semibold uppercase tracking-widest transition-colors ${format === f ? "border-red-500 bg-red-600/20 text-red-400" : "border-white/10 text-slate-400 hover:border-white/30 hover:text-white"}`}
              >
                {formatLabel(f)}
              </button>
            ))}
          </div>
        </div>

        {films.length === 0 && <p className="py-20 text-center text-slate-500" data-testid="films-empty">{t("films.empty")}</p>}

        <div className="grid grid-cols-2 gap-4 pb-24 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid="films-grid">
          {films.map((f, i) => (
            <Reveal key={f.id} delay={(i % 5) * 0.07}>
              <button
                onClick={() => setSelected(f)}
                data-testid={`film-card-${f.id}`}
                className="poster-frame group relative block aspect-[2/3] w-full overflow-hidden rounded border border-white/10 text-left"
              >
                {f.poster ? (
                  <img src={f.poster} alt={f.title} loading="lazy" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-[#14181f] p-4">
                    <span className="font-display text-center text-xl uppercase text-slate-500">{f.title}</span>
                  </div>
                )}
                <span className="font-mono2 absolute left-2 top-2 rounded bg-black/80 px-2 py-0.5 text-[10px] tracking-widest text-amber-500">
                  {FORMAT_BADGE[f.format]} · {f.year}
                </span>
                <div className="absolute inset-x-0 bottom-0 translate-y-3 bg-gradient-to-t from-black via-black/80 to-transparent p-3 pt-10 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                  <p className="font-display text-base uppercase leading-tight">{f.title}</p>
                  {f.character && <p className="font-serif-it mt-1 text-sm italic text-amber-400">{f.character}</p>}
                  <p className="font-mono2 mt-2 text-[10px] tracking-widest text-slate-400">{t("films.details")} →</p>
                </div>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <FilmModal film={selected} onClose={() => setSelected(null)} />
      <Footer />
    </div>
  );
}
